import React, { useEffect, useState } from 'react';
import { Box, Grid, Text, CheckBox, TextInput, Button, Tip } from 'grommet';
import { FTSelect, FTTextArea, FTTextInput } from '../../../components/inputs';
import { FormClose, CircleQuestion } from 'grommet-icons';
import * as itemTypes from '../../../items/item-types';
import { authorizedFetch, getItemsList } from '../../../utils';
import ConfigurationTemplateButton from './configuration-template-button';
import AdUnitTemplatePickerModal from './ad-unit-template-picker-modal';
import PlacementExperience from '../../../components/placement-experience';
import IntegrationScriptBody from '../integration-script-body';
import MessageBox from '../../../components/messageBox';
import MinRpsFloorInput from '../components/MinRpsFloorInput';

export const IMPLEMENTATION_TYPES = [
  { label: 'Page Overlay', value: 'pageoverlay' },
  { label: 'Embed on Page', value: 'embedonpage' }
];

export const ABOVE_THE_FOLD_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'Unknown', value: 'unknown' }
];

export const getFlowPublications = async (flowId, previewOnly = false) => {
  try {
    let response = await authorizedFetch(
      `/api/flow/${flowId}/publications?previewOnly=${previewOnly}`,
      'GET'
    );

    return response;
  } catch (e) {
    throw new Error(e);
  }
};

export const getPartnerTrafficSources = async trafficPartnerId => {
  try {
    const response = await authorizedFetch(
      'api/search/eventflow-trafficsourcereadmodel/_search',
      'POST',
      {
        _source: ['id', 'name'],
        size: 10000,
        sort: {
          dateModified: 'desc'
        },
        query: {
          term: {
            'trafficPartnerId.keyword': trafficPartnerId
          }
        }
      }
    );
    const trafficSources = response?.hits?.hits
      ? response.hits.hits.map(d => d._source)
      : [];
    const remappedSourceList = trafficSources.map(source => {
      return { label: source.name, id: source.id };
    });
    return remappedSourceList;
  } catch (e) {
    throw new Error(e.error.reason);
  }
};

const EmbeddedFlowConfigInfo = props => {
  const {
    item,
    modifyItem,
    addError,
    addSuccess,
    canEdit,
    adUnitTemplates,
    customAdUnitTemplates,
    addMessage,
    removeToast
  } = props;

  const {
    trafficSourceId,
    flowId,
    publishedFlowUrl,
    requestingUrlContainsFilters,
    enabled,
    trafficPartnerId,
    implementationType,
    adUnitNumberOffers,
    adUnitTemplateId,
    adUnitOptimizelyIntegration,
    adUnitOptimizelyProjectId,
    campaignPlacementId,
    placementExperienceId,
    placementLocationId
  } = item;

  const [trafficSourceList, setTrafficSourceList] = useState([]);
  const [filteredTrafficSourceList, setFilteredTrafficSourceList] = useState(
    []
  );
  const [flowList, setFlowList] = useState([]);
  const [filteredFlowList, setFilteredFlowList] = useState([]);
  const [placementList, setPlacementList] = useState([]);
  const [filteredPlacementList, setFilteredPlacementList] = useState([]);
  const [domainList, setDomainList] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const partnerTrafficSources = await getPartnerTrafficSources(
          trafficPartnerId
        );

        const fetchedFlowList = await getItemsList(itemTypes.FLOW);
        const fetchedPlacementList = await getItemsList(
          itemTypes.CAMPAIGN_PLACEMENT
        );

        const remappedFlowList = fetchedFlowList.map(flow => {
          return { label: flow.name, id: flow.id };
        });
        const remappedPlacementList = fetchedPlacementList.map(placement => {
          return { label: placement.name, id: placement.id };
        });

        setTrafficSourceList(partnerTrafficSources);
        setFilteredTrafficSourceList(partnerTrafficSources);
        setFlowList(remappedFlowList);
        setFilteredFlowList(remappedFlowList);
        setPlacementList(remappedPlacementList);
        setFilteredPlacementList(remappedPlacementList);
      } catch (e) {
        addError('There was an error getting config data', e.message);
      }
    };

    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (flowId) {
      const loadDomains = async () => {
        const fetchedFlowPublications = await getFlowPublications(flowId);
        setDomainList(
          fetchedFlowPublications.map(pub => {
            return { label: pub.publicationName, url: pub.url };
          })
        );
      };

      loadDomains();
    }
  }, [flowId]);

  const addNewFilter = () => {
    const newFilterList = [
      ...requestingUrlContainsFilters,
      {
        url: '',
        useForIntegration: false
      }
    ];
    modifyItem({
      requestingUrlContainsFilters: newFilterList
    });
  };

  return (
    <Box fill overflow={{ vertical: 'auto' }} pad='medium'>
      <Grid
        columns={['repeat(3, minmax(250px, 1fr))']}
        gap='medium'
        justifyContent='start'
      >
        <Box gridArea='auto/span 3'>
          <ConfigurationTemplateButton
            adUnitTemplates={adUnitTemplates}
            customAdUnitTemplates={customAdUnitTemplates}
            embeddedFlowConfig={item}
            setOpenModal={setOpenModal}
            addSuccess={addSuccess}
            canEdit={canEdit}
          />
        </Box>
        <FTTextInput
          disabled={!canEdit}
          title='Name'
          label='Name'
          value={item.name}
          onChange={value => {
            modifyItem({ name: value });
          }}
        />
        <FTTextArea
          label='Description'
          disabled={!canEdit}
          value={item.description || ''}
          onChange={newValue => {
            modifyItem({
              description: newValue
            });
          }}
        />
        {adUnitTemplateId === null && (
          <FTSelect
            disabled={!canEdit}
            title={`Select a flow`}
            placeholder={`Select a flow`}
            labelKey='label'
            valueKey='id'
            options={filteredFlowList}
            value={flowList.find(f => f.id === flowId)}
            label='Assigned Flow'
            onChange={({ id }) => {
              modifyItem({
                flowId: id
              });
            }}
            handleSearch={search => {
              const lowercaseSearch = search.toLowerCase();
              if (search === '') {
                setFilteredFlowList(flowList);
              } else {
                setFilteredFlowList(
                  flowList.filter(flow => {
                    const flowLabel = flow.label.toLowerCase();
                    return flowLabel.includes(lowercaseSearch);
                  })
                );
              }
            }}
            onClose={() => {
              setFilteredFlowList(flowList);
            }}
          />
        )}
        {adUnitTemplateId === null && (
          <FTSelect
            title={`Select a domain`}
            placeholder={`Select a domain`}
            labelKey='label'
            valueKey='url'
            options={domainList}
            value={domainList.find(d => d.url === publishedFlowUrl)}
            label='Assigned Flow Domain'
            onChange={({ url }) => {
              modifyItem({
                publishedFlowUrl: url
              });
            }}
            disabled={!flowId || !canEdit}
          />
        )}
        {adUnitTemplateId === null && (
          <FTSelect
            title={`Select implementation type`}
            placeholder={`Select implementation type`}
            labelKey='label'
            valueKey='value'
            options={IMPLEMENTATION_TYPES}
            value={IMPLEMENTATION_TYPES.find(
              it => it.value === implementationType
            )}
            label='Implementation Type'
            onChange={({ value }) => {
              modifyItem({
                implementationType: value
              });
            }}
            disabled={!flowId || !canEdit}
          />
        )}
        <Box direction='column' gap='small'>
          <Text>Status</Text>
          <CheckBox
            disabled={!canEdit || (!!flowId && !adUnitTemplateId && !enabled)}
            toggle
            checked={enabled}
            onChange={event => {
              modifyItem({ enabled: event.target.checked });
            }}
          />
          {enabled && <Text color='green'>Active</Text>}
        </Box>
        <Box gap='small' gridArea='auto/span 3'>
          <Box
            border={{ side: 'top', color: 'border400' }}
            fill='horizontal'
            margin={{ top: 'medium' }}
          />
          <MessageBox
            type='alert'
            message='For already implemented clients - any calls to fc.show() assumes Order Confirmation and won’t detect other configurations. Before changing any in-use active configuration away from Order Confirmation YOU MUST work with the partner first to update their call to the ID version or risk not displaying the ad unit.'
          />
          <Grid columns={['repeat(3, minmax(250px, 1fr))']} gap='medium'>
            <FTSelect
              disabled={!canEdit}
              title={`Select a traffic source`}
              placeholder={
                trafficSourceList.length > 0
                  ? `Select a traffic source`
                  : 'Assign or create a traffic source for this partner'
              }
              labelKey='label'
              valueKey='id'
              options={filteredTrafficSourceList}
              value={trafficSourceList.find(s => s.id === trafficSourceId)}
              label='Traffic Source'
              onChange={({ id }) => {
                modifyItem({
                  trafficSourceId: id
                });
              }}
              handleSearch={search => {
                const lowercaseSearch = search.toLowerCase();
                if (search === '') {
                  setFilteredTrafficSourceList(trafficSourceList);
                } else {
                  setFilteredTrafficSourceList(
                    trafficSourceList.filter(source => {
                      const sourceLabel = source.label.toLowerCase();
                      return sourceLabel.includes(lowercaseSearch);
                    })
                  );
                }
              }}
              onClose={() => {
                setFilteredTrafficSourceList(trafficSourceList);
              }}
            />
            <PlacementExperience
              disabled={!canEdit}
              placementExperienceId={placementExperienceId}
              placementLocationId={placementLocationId}
              onChange={({ placementExperienceId, placementLocationId }) => {
                modifyItem({
                  placementExperienceId,
                  placementLocationId
                });
              }}
              addError={addError}
            />
            <FTSelect
              disabled={!canEdit}
              title={`Select a above the fold`}
              placeholder='Select a above the fold'
              labelKey='label'
              valueKey='value'
              options={ABOVE_THE_FOLD_OPTIONS}
              value={ABOVE_THE_FOLD_OPTIONS.find(
                it => it.value === item.aboveTheFold || it.value === 'unknown'
              )}
              label='Above the fold'
              onChange={({ value }) => {
                modifyItem({
                  aboveTheFold: value
                });
              }}
            />
            <MinRpsFloorInput
              initialValue={item.minRpsFloor}
              disabled={!canEdit}
              itemId={item.id}
              onValidationChange={props.setHasValidationError}
              onChange={value => modifyItem({ minRpsFloor: value })}
            />
          </Grid>
        </Box>
        <Box gap='xsmall' margin={{ top: 'medium' }} gridArea='auto/span 2'>
          <Box justify='between' direction='row'>
            <Text>Publisher Website Targeting</Text>
            <Button onClick={addNewFilter} disabled={!canEdit}>
              + Add New
            </Button>
          </Box>
          {requestingUrlContainsFilters.map((filter, index) => {
            return (
              <Box key={index} direction='row' align='center'>
                <Box width='120px'>
                  {index === 0 ? (
                    <Text>URL contains:</Text>
                  ) : (
                    <Text>OR contains:</Text>
                  )}
                </Box>
                <Box
                  width='50%'
                  direction='row'
                  align='center'
                  border={{ color: 'border400' }}
                  round='xsmall'
                >
                  <TextInput
                    disabled={!canEdit}
                    plain
                    value={filter.url ?? ''}
                    onChange={({ target }) => {
                      const newFilters = [...requestingUrlContainsFilters];
                      newFilters[index] = {
                        url: target.value,
                        useForIntegration: filter.useForIntegration ?? false
                      };
                      modifyItem({
                        requestingUrlContainsFilters: [...newFilters]
                      });
                    }}
                  />
                  {index !== 0 && (
                    <Button
                      disabled={!canEdit}
                      style={{ display: 'flex' }}
                      plain
                      onClick={() => {
                        const newFilters = [...requestingUrlContainsFilters];
                        newFilters.splice(index, 1);
                        modifyItem({
                          requestingUrlContainsFilters: [...newFilters]
                        });
                      }}
                    >
                      <FormClose />
                    </Button>
                  )}
                </Box>
                {adUnitTemplateId !== null && (
                  <Box pad='small'>
                    <CheckBox
                      disabled={!canEdit}
                      label='use for integration testing (pre-prod env)'
                      checked={filter.useForIntegration}
                      onChange={event => {
                        const newFilters = [...requestingUrlContainsFilters];
                        newFilters[index] = {
                          ...newFilters[index],
                          useForIntegration: event.target.checked
                        };
                        modifyItem({
                          requestingUrlContainsFilters: [...newFilters]
                        });
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Grid>

      {adUnitTemplateId !== null && (
        <Grid columns={['repeat(1, minmax(300px, 1fr))']}>
          <Box
            border={{ side: 'top', color: 'border400' }}
            fill='horizontal'
            margin={{ vertical: 'medium' }}
            gridArea='auto/span 3'
          />
          <Box direction='column' gap='small' fill='horizontal'>
            <Text weight='bold'>Offer Configuration</Text>
            <Box
              direction='row'
              gap='small'
              fill='horizontal'
              margin={{ bottom: 'medium' }}
            >
              <FTSelect
                disabled={!canEdit}
                title={`Select a placement`}
                placeholder={`Select a placement`}
                labelKey='label'
                valueKey='id'
                options={filteredPlacementList}
                value={placementList.find(p => p.id === campaignPlacementId)}
                label='Placement'
                onChange={({ id }) => {
                  modifyItem({
                    campaignPlacementId: id
                  });
                }}
                handleSearch={search => {
                  const lowercaseSearch = search.toLowerCase();
                  if (search === '') {
                    setFilteredPlacementList(placementList);
                  } else {
                    setFilteredPlacementList(
                      placementList.filter(placement => {
                        const placementLabel = placement.label.toLowerCase();
                        return placementLabel.includes(lowercaseSearch);
                      })
                    );
                  }
                }}
                onClose={() => {
                  setFilteredPlacementList(placementList);
                }}
              />
              <FTTextInput
                disabled={!canEdit}
                label='Quantity of offers shown (only editable by an admin)'
                value={adUnitNumberOffers}
                min={1}
                max={20}
                type='number'
                onChange={value => {
                  modifyItem({ adUnitNumberOffers: parseInt(value) });
                }}
              />
            </Box>
          </Box>
          <Box
            border={{ side: 'top', color: 'border400' }}
            gridArea='auto/span 3'
            margin={{ vertical: 'medium' }}
          />
          <Box gap='medium'>
            <Box direction='row' gap='small'>
              <Text>
                Test with Optimizely{' '}
                <Tip
                  width='medium'
                  content='If Active, the Optimizely script will be included in the configuration template.'
                >
                  <span>
                    <CircleQuestion size='small' />
                  </span>
                </Tip>
              </Text>
              <CheckBox
                disabled={!canEdit}
                toggle
                checked={adUnitOptimizelyIntegration}
                onChange={event => {
                  modifyItem({
                    adUnitOptimizelyIntegration: event.target.checked
                  });
                }}
              />
              {adUnitOptimizelyIntegration && <Text color='green'>Active</Text>}
            </Box>
            <Box width='large'>
              <FTTextInput
                disabled={!canEdit || !adUnitOptimizelyIntegration}
                title='Optimizely Project ID'
                label='Optimizely Project ID (Optional - leave blank to use default)'
                value={adUnitOptimizelyProjectId || ''}
                onChange={value => {
                  modifyItem({ adUnitOptimizelyProjectId: value || null });
                }}
                placeholder='Enter project ID to override the default'
              />
            </Box>
          </Box>
        </Grid>
      )}

      <Grid columns={['repeat(1, minmax(300px, 1fr))']} gap='small'>
        <Box
          border={{ side: 'top', color: 'border400' }}
          gridArea='auto/span 3'
          margin={{ top: 'medium', bottom: 'small' }}
        />
        <Box
          gridArea='auto/span 3'
          margin={{
            bottom: 'large'
          }}
        >
          <IntegrationScriptBody
            item={item}
            addSuccess={addSuccess}
            templates={[...adUnitTemplates, ...customAdUnitTemplates]}
          />
        </Box>
      </Grid>

      {openModal && (
        <AdUnitTemplatePickerModal
          modifyItem={modifyItem}
          setModalOpen={setOpenModal}
          adUnitTemplates={adUnitTemplates}
          customAdUnitTemplates={customAdUnitTemplates}
          addMessage={addMessage}
          currentTemplate={adUnitTemplateId}
          wasLegacyTemplate={!!flowId}
          publishedFlowUrl={publishedFlowUrl}
          requestingUrlContainsFilters={requestingUrlContainsFilters}
          adUnitNumberOffers={adUnitNumberOffers}
          flowId={flowId}
          removeToast={removeToast}
        />
      )}
    </Box>
  );
};

EmbeddedFlowConfigInfo.displayName = 'EmbeddedFlowConfigInfo';

export default EmbeddedFlowConfigInfo;
